import React, { FC, useContext, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  Grid
} from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Add } from '@mui/icons-material';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import { useTranslation } from 'react-i18next';
import { Can } from '@casl/react';
import AbilityContext from '@/contexts/AbilityContext';
import { Action } from '@/types/permissions';
import type { KdsStation } from '@/types/Table.interface';
import NoDataFound from '@/components/NoDataFound';
import StatusLabel from '@/components/StatusLabel';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';

interface KdsStationsTableProps {
  stations: KdsStation[];
  categoryNames: Map<number, string>;
  loading: boolean;
  onAdd: () => void;
  onEdit: (station: KdsStation) => void;
  onDelete: (id: number) => void;
  onRefresh: () => void;
}

const KdsStationsTable: FC<KdsStationsTableProps> = ({
  stations,
  categoryNames,
  loading,
  onAdd,
  onEdit,
  onDelete,
  onRefresh
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ability = useContext(AbilityContext);

  const [page, setPage] = React.useState(0);
  const [limit, setLimit] = React.useState(10);

  const sortedStations = useMemo(
    () => [...stations].sort((a, b) => a.sortOrder - b.sortOrder),
    [stations]
  );
  const lastPage = Math.max(0, Math.ceil(sortedStations.length / limit) - 1);
  const visiblePage = Math.min(page, lastPage);
  React.useEffect(() => {
    setPage(visiblePage);
  }, [visiblePage]);
  const paginatedStations = sortedStations.slice(
    visiblePage * limit,
    visiblePage * limit + limit
  );

  const tableHeaders = [
    { id: 'name', label: t('table.kds.station.name'), align: 'left' as const },
    {
      id: 'categories',
      label: t('table.kds.station.categories'),
      align: 'left' as const
    },
    { id: 'sortOrder', label: t('table.sort.order'), align: 'center' as const },
    { id: 'status', label: t('status'), align: 'center' as const },
    { id: 'actions', label: t('actions'), align: 'center' as const }
  ];

  const renderCategories = (station: KdsStation) => {
    const ids = station.categoryIds ?? [];
    if (ids.length === 0) {
      return (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={t('table.kds.station.all.categories')}
        />
      );
    }
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {ids.map((id) => (
          <Chip
            key={id}
            size="small"
            label={categoryNames.get(id) ?? `#${id}`}
          />
        ))}
      </Box>
    );
  };

  return (
    <Container disableGutters maxWidth={false} sx={{ maxWidth: '90%' }}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Card>
            <CardHeader
              action={
                <Can I={Action.Create} a="KdsStation" ability={ability}>
                  <Button
                    startIcon={<Add />}
                    onClick={onAdd}
                    variant="contained"
                    color="primary"
                  >
                    {t('table.kds.station.create')}
                  </Button>
                </Can>
              }
              title={
                <Box display="flex" alignItems="center">
                  <Typography variant="h4" component="span">
                    {t('table.kds.stations')}
                  </Typography>
                  <Tooltip arrow title={t('refresh.list')}>
                    <IconButton
                      onClick={onRefresh}
                      sx={{
                        ml: 1,
                        '&:hover': {
                          background: theme.colors.primary.lighter
                        },
                        color: theme.palette.primary.main
                      }}
                    >
                      <RefreshTwoToneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header.id} align={header.align}>
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length}>
                        <Box p={2} display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : paginatedStations.length === 0 ? (
                    <NoDataFound
                      message={t('table.kds.station.no.data')}
                      colSpan={tableHeaders.length}
                    />
                  ) : (
                    paginatedStations.map((station) => (
                      <TableRow key={station.id} hover>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">
                            {station.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{renderCategories(station)}</TableCell>
                        <TableCell align="center">
                          {station.sortOrder}
                        </TableCell>
                        <TableCell align="center">
                          <StatusLabel
                            status={station.isActive ? 'ACTIVE' : 'PASSIVE'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Can
                            I={Action.Update}
                            a="KdsStation"
                            ability={ability}
                          >
                            <Tooltip title={t('table.kds.station.edit')} arrow>
                              <IconButton
                                onClick={() => onEdit(station)}
                                sx={{
                                  '&:hover': {
                                    background: theme.colors.primary.lighter
                                  },
                                  color: theme.palette.primary.main
                                }}
                                color="inherit"
                                size="small"
                              >
                                <EditTwoToneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Can>
                          <Can
                            I={Action.Delete}
                            a="KdsStation"
                            ability={ability}
                          >
                            <Tooltip
                              title={t('table.kds.station.delete')}
                              arrow
                            >
                              <IconButton
                                onClick={() => onDelete(station.id)}
                                sx={{
                                  '&:hover': {
                                    background: theme.colors.error.lighter
                                  },
                                  color: theme.palette.error.main
                                }}
                                color="inherit"
                                size="small"
                              >
                                <DeleteTwoToneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Can>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <TablePagination
                component="div"
                count={sortedStations.length}
                onPageChange={(_e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setLimit(Number.parseInt(e.target.value));
                  setPage(0);
                }}
                page={visiblePage}
                rowsPerPage={limit}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default KdsStationsTable;
